import React, { JSX, useEffect } from 'react';
import {
  ChevronLeft,
  CircleCheckIcon,
  LeafIcon,
  Recycle,
  Zap,
  Droplets,
  Accessibility,
  ShoppingCart,
  Users,
  Building2,
  FileCheck,
  Target,
  GraduationCap,
  Thermometer,
} from 'lucide-react';
import {
  BackButton,
  StepsContainer,
  ProgressContainer,
  ProgressBar,
  ProgressFill,
  StepList,
  StepItem,
  StepIcon,
  StepContent,
  StepTitle,
  DialogSidebar,
  StepConnector,
} from './index';
import { NavigationConfirmModal } from '../NavigationConfirmModal';
import {
  BankIcon,
  CertificateIcon,
  ExportFileIcon,
  HotelIcon,
  NaturePeopleIcon,
  PeopleIcon,
  PersonOutlineIcon,
} from '../../../assets/icons';
import {
  useHotelQuestionnaireData,
  useHotelQuestionnaireSteps,
  useNavigationConfirmation,
} from '../../../hooks';
import { useSplash } from '../../../contexts/SplashContext';

//constant map object to hold step icons
const stepIconMap: Record<string, JSX.Element> = {
  'hotel-location': <HotelIcon />,
  'your-details': <PersonOutlineIcon />,
  'hotel-operations': <HotelIcon />,
  certifications: <CertificateIcon />,
  planet: <NaturePeopleIcon />,
  people: <PeopleIcon />,
  policy: <BankIcon />,
  sources: <ExportFileIcon />,
  biodiversity: <LeafIcon />,
  climate: <Thermometer />,
  energy: <Zap />,
  'water-stewardship': <Droplets />,
  waste: <Recycle />,
  accessibility: <Accessibility />,
  'responsible-sourcing': <ShoppingCart />,
  'human-rights,-justice-+-wellbeing': <Users />,
  'community-+-cultural-stewardship': <Building2 />,
  auditability: <FileCheck />,
  'strategy-+-management': <Target />,
  'training-+-engagement': <GraduationCap />,
};

interface HotelDetailSidebarProps {
  onClose: () => void;
  setActiveTab: (tab: 'submit' | 'travel' | 'score') => void;
  isBoostHotel: boolean;
  navigationOptions?: {
    scrollToQuestion?: (questionId: string) => void;
    setUnansweredQuestions?: (questionIds: string[], forValidation?: boolean) => void;
    setQuestionErrors?: (errors: Record<string, string>) => void;
  };
}

const MAX_STEPS = 7;

const HotelDetailSidebar: React.FC<HotelDetailSidebarProps> = ({
  onClose,
  setActiveTab,
  isBoostHotel,
  navigationOptions,
}) => {
  const { stepsData, goToStep } = useHotelQuestionnaireSteps();
  const { questionnaireLoading } = useHotelQuestionnaireData();
  const {
    showConfirmModal,
    pendingNavigation,
    isConfirmingNavigation,
    isSavingNavigation,
    requestNavigation,
    cancelNavigation,
    confirmNavigation,
    saveAndNavigate,
  } = useNavigationConfirmation(navigationOptions);
  const { requestCertificationSplash, requestProgressMilestoneSplash } =
    useSplash();

  useEffect(() => {
    if (stepsData?.currentStepKey === 'certifications' && !isBoostHotel) {
      requestCertificationSplash();
    }
    if (
      stepsData?.completedSteps &&
      stepsData?.completedSteps === 4 &&
      !isBoostHotel
    ) {
      requestProgressMilestoneSplash(stepsData.hotelId);
    }
  }, [stepsData?.completedSteps, stepsData?.currentStepKey, stepsData?.hotelId, isBoostHotel]);

  const handleStepClick = (stepKey: string, stepTitle: string) => {
    setActiveTab('submit');
    // Prevent navigation if questionnaire is loading or if already on this step
    if (questionnaireLoading || stepKey === stepsData?.currentStepKey) {
      return;
    }

    const canNavigate = requestNavigation(stepKey, stepTitle);
    if (canNavigate) {
      goToStep(stepKey);
    }
  };

  const handleCloseClick = () => {
    const canClose = requestNavigation('close', 'Home Page');
    if (canClose) {
      onClose();
    }
  };

  if (!stepsData) {
    return <div>Failed to load data</div>;
  }

  return (
    <>
      <DialogSidebar className="fade-in">
        <BackButton onClick={handleCloseClick}>
          <ChevronLeft
            size={20}
            color="var(--color-gray-600)"
            strokeWidth={2}
          />
          <h2>Properties</h2>
        </BackButton>
        <StepsContainer>
          <ProgressContainer>
            <h1>Form Progress</h1>
            <ProgressBar>
              <ProgressFill $progress={stepsData.progress} />
            </ProgressBar>
            <p>
              {stepsData.completedSteps > MAX_STEPS
                ? MAX_STEPS
                : stepsData.completedSteps}{' '}
              of {stepsData.totalSteps} completed
            </p>
          </ProgressContainer>

          <StepList className="custom-scrollbar">
            {stepsData.steps.map((step, index) => {
              const isLastStep = index === stepsData.steps.length - 1;
              const isActive = step.key === stepsData.currentStepKey;
              const activeStepIndex = stepsData.steps.findIndex(
                s => s.key === stepsData.currentStepKey
              );
              const isFlowing = activeStepIndex === index;
              const isDisabled = questionnaireLoading;

              return (
                <StepItem
                  key={step.id}
                  $isActive={isActive}
                  $isCompleted={step.isCompleted}
                  $isDisabled={isDisabled}
                >
                  <StepIcon
                    $isActive={isActive}
                    $isCompleted={step.isCompleted}
                    $isDisabled={isDisabled}
                    onClick={() => handleStepClick(step.key, step.title)}
                  >
                    {stepIconMap[step.key]}
                    {!isLastStep && (
                      <StepConnector
                        $isActive={isActive}
                        $isFlowing={isFlowing}
                        $isCompleted={step.isCompleted}
                      >
                        {isFlowing && <div className="connector-fill" />}
                      </StepConnector>
                    )}
                  </StepIcon>
                  <StepContent className="">
                    <StepTitle
                      $isActive={isActive}
                      $isCompleted={step.isCompleted}
                      $isDisabled={isDisabled}
                      onClick={() => handleStepClick(step.key, step.title)}
                    >
                      {step.title}
                    </StepTitle>
                    {step.isCompleted && (
                      <CircleCheckIcon
                        fill="var(--color-success)"
                        color="var(--color-white)"
                        strokeWidth={2}
                      />
                    )}
                  </StepContent>
                </StepItem>
              );
            })}
          </StepList>
        </StepsContainer>
      </DialogSidebar>

      <NavigationConfirmModal
        isOpen={showConfirmModal}
        onClose={cancelNavigation}
        onConfirm={async () => {
          const isClosing = pendingNavigation?.stepKey === 'close';
          await confirmNavigation();
          if (isClosing) {
            onClose();
          }
        }}
        onSave={async () => {
          const isClosing = pendingNavigation?.stepKey === 'close';
          await saveAndNavigate();
          if (isClosing) {
            onClose();
          }
        }}
        targetStep={pendingNavigation?.stepName}
        isConfirmingNavigation={isConfirmingNavigation}
        isSavingNavigation={isSavingNavigation}
      />
    </>
  );
};

export default HotelDetailSidebar;
